// ⚠️ 重要：本プライバシーポリシーは実際のサービス運用前に必ず弁護士のレビューを受けてください。
// 本ファイルはドラフトであり、現状のままでの法的有効性は保証されません。
//
// TODO (本番デプロイ前に実値へ置換):
//   - [会社名]
//   - [管理者氏名]
//   - support@kagem.ai
//   - 認定個人情報保護団体への加入有無
//   - 第三者AIサービス／インフラ各社のプライバシーポリシーURLが最新か再確認

export const PRIVACY_LAST_UPDATED = "2026年5月1日";

export const PRIVACY_PREAMBLE =
  "KAGEM（以下「本サービス」といいます）における個人情報の取扱いについて、以下のとおりプライバシーポリシー（以下「本ポリシー」といいます）を定めます。";

export interface PrivacyParagraph {
  type: "p";
  text: string;
}

export interface PrivacyList {
  type: "ul" | "ol";
  items: Array<string | PrivacyLinkItem>;
}

export interface PrivacyLinkItem {
  text: string;
  links?: Array<{ label: string; href: string }>;
}

export interface PrivacySubsection {
  type: "subsection";
  heading: string;
  blocks: PrivacyBlock[];
}

export type PrivacyBlock = PrivacyParagraph | PrivacyList | PrivacySubsection;

export interface PrivacySection {
  id: string;
  number: number;
  title: string;
  blocks: PrivacyBlock[];
}

export const PRIVACY_SECTIONS: PrivacySection[] = [
  {
    id: "section-1",
    number: 1,
    title: "個人情報の定義",
    blocks: [
      {
        type: "p",
        text: "本ポリシーにおける「個人情報」とは、個人情報の保護に関する法律（以下「個人情報保護法」といいます）に定義される個人情報を指します。",
      },
    ],
  },
  {
    id: "section-2",
    number: 2,
    title: "収集する個人情報",
    blocks: [
      { type: "p", text: "当社は、本サービスの提供にあたり、以下の個人情報を取得します。" },
      {
        type: "ul",
        items: [
          "氏名、メールアドレス、電話番号",
          "決済情報(Stripeにて処理され、当社サーバーには保存しません)",
          "アップロードされた画像・動画素材",
          "生成された動画",
          "利用履歴、生成履歴、IPアドレス、ブラウザ情報、デバイス情報",
          "同意取得時の記録(IPアドレス、User-Agent、同意項目)",
          "お問い合わせ・通報内容",
        ],
      },
    ],
  },
  {
    id: "section-3",
    number: 3,
    title: "個人情報の利用目的",
    blocks: [
      { type: "p", text: "当社は、取得した個人情報を以下の目的で利用します。" },
      {
        type: "ul",
        items: [
          "本サービスの提供(動画生成、アカウント管理)",
          "認証およびアカウント管理",
          "課金・決済処理",
          "動画生成(第三者AIサービスの利用を含む)",
          "カスタマーサポート対応",
          "不正利用の防止、規約違反の調査",
          "サービス改善(統計情報の集計)",
          "お知らせ・キャンペーン情報の配信(同意取得済みユーザーのみ)",
          "法令に基づく対応",
        ],
      },
    ],
  },
  {
    id: "section-4",
    number: 4,
    title: "個人情報の第三者提供",
    blocks: [
      {
        type: "p",
        text: "本サービスは、サービス提供のために以下の第三者にユーザーのデータ（画像・動画・プロンプト等）を提供します。各事業者の所在国・適用される個人情報保護制度の概要・データの利用目的を明示します。",
      },
      {
        type: "subsection",
        heading: "(a) 動画・画像生成のための第三者AIサービスへの提供",
        blocks: [
          {
            type: "p",
            text: "【Kling AI】 運営事業者：Kuaishou Technology（快手科技） / データ所在地：シンガポールおよび中国 / 提供データ：ユーザーがアップロードした人物画像＋ベース動画 / 利用目的：動画生成処理。適用制度：シンガポール個人データ保護法（PDPA）および中華人民共和国個人情報保護法（PIPL、2021年11月施行）。これらは本人同意・最小限収集・利用目的明示等を規定するものの、日本の個人情報保護法と完全に同等ではなく、中国においては当局によるデータアクセス権限が認められている点にご留意ください。",
          },
          {
            type: "ul",
            items: [
              {
                text: "Kling AI プライバシーポリシー",
                links: [{ label: "klingai.com/privacy", href: "https://klingai.com/privacy" }],
              },
              {
                text: "各国制度の概要は個人情報保護委員会のウェブサイトをご参照ください",
                links: [
                  {
                    label: "個人情報保護委員会",
                    href: "https://www.ppc.go.jp/personalinfo/legal/kaiseihogohou/",
                  },
                ],
              },
            ],
          },
          {
            type: "p",
            text: "【HeyGen】 運営事業者：HeyGen Inc. / データ所在地：米国 / 提供データ：人物画像＋ベース動画 / 利用目的：リップシンク動画生成。米国（連邦）には日本の個人情報保護法に相当する包括法は無く、州法（CCPA/CPRA等）が個別に規律しています。本サービスは契約上のセーフガード（DPA：Data Processing Agreement）を締結しています。",
          },
          {
            type: "ul",
            items: [
              {
                text: "HeyGen プライバシーポリシー",
                links: [{ label: "heygen.com/privacy", href: "https://heygen.com/privacy" }],
              },
            ],
          },
          {
            type: "p",
            text: "【Akool】 運営事業者：Akool Inc. / データ所在地：米国 / 提供データ：人物画像＋ベース動画 / 利用目的：顔交換動画生成。適用制度はHeyGenと同様。",
          },
          {
            type: "ul",
            items: [
              {
                text: "Akool プライバシーポリシー",
                links: [{ label: "akool.com/privacy", href: "https://akool.com/privacy" }],
              },
            ],
          },
          {
            type: "p",
            text: "【Replicate】 運営事業者：Replicate Inc. / データ所在地：米国 / 提供データ：NSFW検出用の入力画像 / 利用目的：コンテンツモデレーション。適用制度はHeyGenと同様。",
          },
          {
            type: "ul",
            items: [
              {
                text: "Replicate プライバシーポリシー",
                links: [{ label: "replicate.com/privacy", href: "https://replicate.com/privacy" }],
              },
            ],
          },
          {
            type: "p",
            text: "【ElevenLabs】 運営事業者：ElevenLabs Inc. / データ所在地：米国 / 提供データ：動画から抽出した音声ファイル / 利用目的：音声変換処理（Speech to Speech）。適用制度はHeyGenと同様（米国州法＋契約上のセーフガード）。",
          },
          {
            type: "ul",
            items: [
              {
                text: "ElevenLabs プライバシーポリシー",
                links: [{ label: "elevenlabs.io/privacy", href: "https://elevenlabs.io/privacy" }],
              },
            ],
          },
          {
            type: "p",
            text: "【Google（Lovable AI Gateway 経由）】 運営事業者：Google LLC / データ所在地：米国 / 提供データ：プロンプト＋参照画像 / 利用目的：画像生成・プロンプト翻訳。GoogleはSCC（Standard Contractual Clauses）等を通じた高水準のデータ保護を実施しています。",
          },
          {
            type: "ul",
            items: [
              {
                text: "Google プライバシーポリシー",
                links: [
                  { label: "policies.google.com/privacy", href: "https://policies.google.com/privacy" },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "subsection",
        heading: "(b) 運営インフラのための第三者提供",
        blocks: [
          {
            type: "ul",
            items: [
              {
                text: "Stripe（決済処理、米国）",
                links: [{ label: "プライバシーポリシー", href: "https://stripe.com/jp/privacy" }],
              },
              {
                text: "Supabase（DB・ストレージ・認証、日本リージョン）",
                links: [{ label: "プライバシーポリシー", href: "https://supabase.com/privacy" }],
              },
              {
                text: "Lovable Cloud（フロントエンド配信・メール送信・AI Gateway、米国）",
                links: [{ label: "プライバシーポリシー", href: "https://lovable.dev/privacy" }],
              },
              {
                text: "Google Analytics（解析、米国）",
                links: [
                  { label: "プライバシーポリシー", href: "https://policies.google.com/privacy" },
                ],
              },
              {
                text: "Meta Platforms, Inc.（Meta Pixel / Facebook広告のコンバージョン計測・リターゲティング、米国）",
                links: [
                  { label: "プライバシーポリシー", href: "https://www.facebook.com/privacy/policy/" },
                ],
              },
            ],
          },
        ],
      },
      {
        type: "subsection",
        heading: "(c) 越境移転についての同意",
        blocks: [
          {
            type: "p",
            text: "本サービスを利用することにより、上記第三者への個人情報の提供（外国への移転を含む）に同意したものとみなします。同意を撤回する場合、本サービスの利用を中止してください。",
          },
        ],
      },
      {
        type: "subsection",
        heading: "(d) 例外",
        blocks: [
          {
            type: "p",
            text: "法令に基づく場合および人の生命・身体・財産の保護に必要な場合は、ご本人の同意なく第三者に提供する場合があります。",
          },
        ],
      },
    ],
  },
  {
    id: "section-5",
    number: 5,
    title: "越境移転について",
    blocks: [
      {
        type: "p",
        text: "前条に記載のとおり、一部の第三者AIサービスおよび運営インフラのデータは、日本国外(米国・中国・シンガポール等)に移転されます(個人情報保護法第28条関連)。各国の個人情報保護制度については、以下を参照してください。",
      },
      {
        type: "ul",
        items: [
          {
            text: "米国・各国制度の概要:",
            links: [{ label: "個人情報保護委員会ウェブサイト", href: "https://www.ppc.go.jp/" }],
          },
          { text: "中国:個人情報保護法(PIPL、2021年施行)" },
        ],
      },
      {
        type: "p",
        text: "本サービス利用にあたり、ユーザーはこれらの越境移転に同意いただいたものとみなします。",
      },
    ],
  },
  {
    id: "section-6",
    number: 6,
    title: "個人情報の安全管理",
    blocks: [
      { type: "p", text: "当社は、取り扱う個人情報の漏洩・滅失・毀損の防止のため、以下の安全管理措置を講じます。" },
      {
        type: "ul",
        items: [
          "暗号化通信(HTTPS / TLS)",
          "データベースは認証必須、Row Level Security(RLS)によるアクセス制御",
          "第三者AIサービスとの通信は暗号化",
          "退職者・契約終了者のアクセス権限を即時剥奪",
          "不正アクセス・漏洩に対する継続的な監視",
        ],
      },
    ],
  },
  {
    id: "section-7",
    number: 7,
    title: "データ保管期間",
    blocks: [
      {
        type: "ul",
        items: [
          "生成された動画:論理削除後30日間保持の後、完全削除",
          "利用履歴:最終ログインから3年間",
          "監査ログ(audit_logs):5年間(紛争対応のため)",
          "退会後:法令遵守に必要な範囲を除き、速やかに削除",
        ],
      },
    ],
  },
  {
    id: "section-8",
    number: 8,
    title: "個人情報の開示・訂正・利用停止・削除請求",
    blocks: [
      {
        type: "p",
        text: "本人またはその代理人からの請求により、保有個人情報の開示・訂正・追加・削除・利用停止・第三者提供の停止に応じます。",
      },
      {
        type: "ul",
        items: [
          {
            text: "請求方法:お問い合わせフォーム または下記メールアドレス",
            links: [
              { label: "お問い合わせフォーム", href: "/contact" },
              { label: "support@kagem.ai", href: "mailto:support@kagem.ai" },
            ],
          },
          "本人確認のうえ、原則として30日以内に対応",
          "手数料:原則無料(ただし開示請求の場合、別途手数料が発生する場合があります)",
        ],
      },
    ],
  },
  {
    id: "section-9",
    number: 9,
    title: "Cookieの使用",
    blocks: [
      {
        type: "ul",
        items: [
          "認証Cookie:ログイン状態維持のため必須",
          "解析Cookie:Google Analytics 4(オプトアウト可)",
          "広告計測Cookie:Meta Pixel(Facebook広告のコンバージョン計測・リターゲティング、オプトアウト可)",
          "機能Cookie:ダークモード等の設定保存",
          "ブラウザ設定でCookieを無効化することで、本サービスの一部機能が制限されることがあります",
        ],
      },
    ],
  },
  {
    id: "section-10",
    number: 10,
    title: "アクセス解析ツール",
    blocks: [
      {
        type: "p",
        text: "本サービスは、Google LLCが提供するGoogle Analytics 4(以下「GA4」といいます)を利用しています。",
      },
      {
        type: "ul",
        items: [
          "データはCookieによって匿名で収集されます",
          "個人を特定する情報は含まれません",
          {
            text: "オプトアウト:",
            links: [
              {
                label: "Google Analytics オプトアウト アドオン",
                href: "https://tools.google.com/dlpage/gaoptout",
              },
              { label: "設定画面(/settings)", href: "/settings" },
            ],
          },
        ],
      },
      {
        type: "subsection",
        heading: "Meta Pixel(Facebook広告)",
        blocks: [
          {
            type: "p",
            text: "本サービスは、Meta Platforms, Inc.が提供するMeta Pixel(Facebook Pixel)を利用し、Facebook・Instagram等の広告に関するコンバージョン計測およびリターゲティング(類似オーディエンス配信)を行っています。これに伴い、ページ閲覧・サインアップ完了・決済開始・購入完了等のイベント情報(Cookie、IPアドレス、ブラウザ情報等を含む)が、米国を含むMetaの拠点に送信されます。",
          },
          {
            type: "ul",
            items: [
              "送信されるデータには、氏名等の直接的な個人識別情報は含まれません(Cookie ID・ハッシュ化された識別子等を通じてMetaが照合する場合があります)",
              "送信先:Meta Platforms, Inc.(米国)",
              {
                text: "オプトアウト:",
                links: [
                  {
                    label: "Facebook 広告設定",
                    href: "https://www.facebook.com/adpreferences/ad_settings",
                  },
                  {
                    label: "Meta プライバシーポリシー",
                    href: "https://www.facebook.com/privacy/policy/",
                  },
                ],
              },
              "ブラウザのCookie無効化、トラッキング防止機能、広告ブロッカー等によっても計測を停止できます",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "section-11",
    number: 11,
    title: "個人情報保護管理者",
    blocks: [
      {
        type: "ul",
        items: [
          "氏名:高橋隆",
          "連絡先:support@kagem.ai",
        ],
      },
    ],
  },
  {
    id: "section-12",
    number: 12,
    title: "認定個人情報保護団体",
    blocks: [
      // TODO: 加入する場合は団体名・連絡先を記載
      {
        type: "p",
        text: "当社は、現時点において認定個人情報保護団体には所属していません。",
      },
    ],
  },
  {
    id: "section-13",
    number: 13,
    title: "お問い合わせ窓口",
    blocks: [
      { type: "p", text: "個人情報の取扱いに関するお問い合わせは、以下までご連絡ください。" },
      {
        type: "ul",
        items: [
          "メール:support@kagem.ai",
          {
            text: "お問い合わせフォーム:",
            links: [{ label: "/contact", href: "/contact" }],
          },
        ],
      },
    ],
  },
  {
    id: "section-14",
    number: 14,
    title: "プライバシーポリシーの変更",
    blocks: [
      {
        type: "ul",
        items: [
          "法令の改正やサービス内容の変更に応じて、本ポリシーを改定する場合があります",
          "重要な変更は、メールまたは本サービス上で事前に告知します",
          "改定後の継続利用をもって、変更後の本ポリシーに同意いただいたものとみなします",
        ],
      },
    ],
  },
];
